import React from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { TakeOffActionsProps } from "./types";
import { useTakeOffMutation } from "@src/features/order/redux";
import { ArcButton, ArcStack } from "@src/core/ui";
import { CancelMissionOrderButton, ModifyMissionOrderButton } from '@src/features/order/components';
import { toast } from 'react-toastify';
import { routesConfig } from '@src/core/routes';
import { useGlobalCamera } from '@src/core/providers/global-camera-provider';
import { toTrackedError, trackError } from '@src/core/sentry';
import { addOptimisticMissionSerial } from '@src/features/drone/redux/actions';

const TakeOffActions: React.FC<TakeOffActionsProps> = (props) => {
  const { isAgree, droneSerial, orderId } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { missionId } = useParams();
  const [takeOff, { isLoading }] = useTakeOffMutation();
  const { connectCamera, activeSource } = useGlobalCamera();

  const onTakeOff = () => {
    if (missionId) {
      takeOff({ missionId }).unwrap().then(() => {
        // Pre-arm the persistent camera with the drone we're about to observe so
        // the incoming Drones page sees a matching active source and skips teardown.
        if (droneSerial) {
          connectCamera({
            serialNumber: droneSerial,
            isViewer: activeSource?.isViewer ?? false,
            isEnRoute: true,
            resetGoHere: activeSource?.resetGoHere ?? (() => {}),
          });
          // Bridge the gap before the backend flips drone status to MISSION
          // (UPDATE_DRONES WS push can lag 10-15s); cleared by DronesSidebar
          // once the real status arrives.
          dispatch(addOptimisticMissionSerial(droneSerial));
        }
        // Pass missionId in the URL so the Drones page resolves the route from
        // already-warm RTK cache (populated by this page's useGetOrderMissionQuery)
        // instead of waiting for the order REST round-trip.
        navigate(`${routesConfig.drones}/?droneSerial=${droneSerial}&orderId=${orderId}&missionId=${missionId}`);
        toast.success('Take Off Success Text');
      }).catch((err: unknown) => {
        trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'takeOffEnRoute' } });
      });
      return;
    }
    toast.error('missionId is undefined');
  };

  if (!missionId) return null;
  
  return (
    <ArcStack spacing={1}>
      <ArcStack spacing={1}>
        <ArcStack direction='row' spacing={1}>
          <ModifyMissionOrderButton missionId={missionId} />
          <CancelMissionOrderButton missionId={missionId} successCallback={() => navigate(routesConfig.orders)} />
        </ArcStack>
        <ArcButton.brand 
          disabled={!isAgree}
          isLoading={isLoading} 
          onClick={onTakeOff}> 
          Take Off 
        </ArcButton.brand>
      </ArcStack>

    </ArcStack>
  );
};

export default TakeOffActions;